import type { SupportedLocale } from '@/types';
import { resolveLocale } from '@/utils/locale';
import { persistLocale, resolveInitialLocale } from '@/utils/localeStorage';
import { useEffect, useState } from 'react';

interface UseLanguageSwitchOptions {
  initialLang?: string;
}

interface UseLanguageSwitchReturn {
  locale: SupportedLocale;
}

/**
 * React hook that manages the current locale as reactive state.
 *
 * Initial locale resolution: `initialLang` prop → stored `language` key → `'en'`.
 *
 * The storage fallback lets the widget render in the right language on hosts
 * that don't set the `lang` attribute. When the host does set it, the attribute
 * always wins — it's an explicit instruction.
 *
 * Also listens for `hanko-menu:lang-switch` CustomEvents on `document`, and
 * persists the resolved locale (in the host's value format) so the choice
 * survives a reload and stays in sync with the host application.
 *
 * Prop changes are detected by comparing the resolved value against the
 * last-known prop-driven value. When they differ, locale updates synchronously
 * during render (React concurrent-safe pattern).
 */
export function useLanguageSwitch(options: UseLanguageSwitchOptions = {}): UseLanguageSwitchReturn {
  const { initialLang } = options;
  const propLocale = initialLang ? resolveLocale(initialLang) : undefined;

  const [locale, setLocale] = useState<SupportedLocale>(() => resolveInitialLocale(initialLang));
  const [prevPropLocale, setPrevPropLocale] = useState<SupportedLocale | undefined>(propLocale);

  // Sync locale when the host-provided lang prop changes.
  // This pattern (setState during render) is the React-recommended way to
  // adjust state based on props without effects. See:
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (propLocale !== undefined && propLocale !== prevPropLocale) {
    setPrevPropLocale(propLocale);
    setLocale(propLocale);
  }

  // Persist so the locale survives reloads and is visible to the host app.
  useEffect(() => {
    persistLocale(locale);
  }, [locale]);

  useEffect(() => {
    function handleLangSwitch(event: Event) {
      const customEvent = event as CustomEvent<{ lang?: unknown }>;
      const newLocale = resolveLocale(customEvent.detail?.lang);
      setLocale(newLocale);
    }

    document.addEventListener('hanko-menu:lang-switch', handleLangSwitch);

    return () => {
      document.removeEventListener('hanko-menu:lang-switch', handleLangSwitch);
    };
  }, []);

  return { locale };
}
